import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  ResponsiveModal, ResponsiveModalBody, ResponsiveModalClose, ResponsiveModalContent, ResponsiveModalDescription,
  ResponsiveModalHeader, ResponsiveModalTitle,
} from '../ui/responsive-modal';
import { spotlightApi } from '../../services/api';
import { spotlightFailure } from './SpotlightMenu';
import { useLabelsQuery, useTeamsQuery } from '../../hooks/queries';
import { PRIORITIES } from '../../lib/constants';

const FIELDS = ['status', 'priority', 'due_within', 'label', 'project', 'team'];

/**
 * Spotlight's rules and limit: a task gets in by itself only by a rule the person sets here
 * ("when a task has the priority Urgent, put it in Urgent"); nothing is added without one.
 */
const SpotlightRules = ({ open, onClose, sections, rules, limit, projects }) => {
  const { t } = useTranslation();
  const { data: labels = [] } = useLabelsQuery();
  const { data: teams = [] } = useTeamsQuery();
  const [draft, setDraft] = useState({ field: 'priority', value: 'p1', section_id: '' });
  const [limitText, setLimitText] = useState(limit ? String(limit) : '');
  // Every Spotlight write refreshes it by itself (services/api.js).
  const fail = (err) => {
    toast.error(spotlightFailure(t, err));
  };

  const options = (field) => {
    if (field === 'status') return [['todo', t('task_statuses.default.not_started')], ['in_progress', t('task_statuses.default.in_progress')]];
    // As the task's priority picker names them ("P1 Critical"; the table's column says "Urgent").
    if (field === 'priority') return ['p1', 'p2', 'p3', 'p4'].map((p) => [p, `${PRIORITIES[p].shortLabel} ${t(PRIORITIES[p].labelKey)}`]);
    if (field === 'label') return (Array.isArray(labels) ? labels : []).map((l) => [l.id, l.name]);
    if (field === 'project') return (projects || []).filter((p) => !p.deleted_at).map((p) => [p.id, p.name]);
    if (field === 'team') return (Array.isArray(teams) ? teams : []).map((tm) => [tm.id, tm.name]);
    return null;  // due_within: a number of days
  };
  const valueName = (rule) => {
    const opts = options(rule.when.field);
    return opts ? (opts.find(([v]) => v === rule.when.value)?.[1] || rule.when.value) : rule.when.value;
  };
  const sectionName = (id) => sections.find((s) => s.id === id)?.name || '';
  // "Priority: Critical": also the rule's name, which a task it put in shows as the reason.
  const ruleName = (rule) => t('spotlight.rule_name', { field: t(`spotlight.fields.${rule.when.field}`), value: valueName(rule) });

  const add = () => {
    const sectionId = draft.section_id || sections[0]?.id;
    if (!sectionId || !String(draft.value || '').trim()) return;
    const name = ruleName({ when: draft });
    spotlightApi.addRule({ name, field: draft.field, value: String(draft.value), section_id: sectionId, enabled: true })
      .catch(fail);
  };
  const toggle = (rule) => spotlightApi.updateRule(rule.id, {
    name: rule.name, field: rule.when.field, value: rule.when.value, section_id: rule.section_id, enabled: !rule.enabled,
  }).catch(fail);
  const saveLimit = () => {
    const n = parseInt(limitText, 10);
    spotlightApi.settings(Number.isFinite(n) && n > 0 ? n : null).catch(fail);
  };
  const draftOptions = options(draft.field);

  return (
    <ResponsiveModal open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <ResponsiveModalContent className="bg-popover border-border md:rounded-xl md:max-w-xl" data-testid="spotlight-rules-dialog">
        <ResponsiveModalHeader className="px-6 border-border">
          <div className="flex-1 min-w-0">
            <ResponsiveModalTitle className="text-lg text-foreground">{t('spotlight.rules')}</ResponsiveModalTitle>
            <ResponsiveModalDescription className="text-sm text-muted-foreground">{t('spotlight.rules_hint')}</ResponsiveModalDescription>
          </div>
          <ResponsiveModalClose className="text-muted-foreground hover:bg-accent hover:text-foreground p-2" />
        </ResponsiveModalHeader>
        <ResponsiveModalBody className="p-6 space-y-5">
          <div className="space-y-2">
            {rules.length === 0 && <p className="text-sm text-muted-foreground">{t('spotlight.no_rules')}</p>}
            {rules.map((rule) => (
              <div key={rule.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2" data-testid="spotlight-rule">
                <Switch checked={rule.enabled} onCheckedChange={() => toggle(rule)} aria-label={rule.name || ''} />
                <span className="flex-1 min-w-0 text-sm text-foreground">
                  {t('spotlight.rule_line', { rule: ruleName(rule), section: sectionName(rule.section_id) })}
                </span>
                <button type="button" onClick={() => spotlightApi.deleteRule(rule.id).catch(fail)}
                  className="p-1 rounded-md text-muted-foreground hover:text-red-500 hover:bg-accent" aria-label={t('common.delete')}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-border p-3 space-y-2">
            <div className="text-xs text-muted-foreground">{t('spotlight.rule_when')}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Select value={draft.field} onValueChange={(field) => setDraft({ ...draft, field, value: (options(field) || [['3']])[0]?.[0] || '' })}>
                <SelectTrigger data-testid="rule-field"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FIELDS.map((f) => <SelectItem key={f} value={f}>{t(`spotlight.fields.${f}`)}</SelectItem>)}
                </SelectContent>
              </Select>
              {draftOptions ? (
                <Select value={draft.value} onValueChange={(value) => setDraft({ ...draft, value })}>
                  <SelectTrigger data-testid="rule-value"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {draftOptions.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input type="number" min={0} max={365} value={draft.value} data-testid="rule-value"
                  onChange={(e) => setDraft({ ...draft, value: e.target.value })} />
              )}
            </div>
            <div className="text-xs text-muted-foreground">{t('spotlight.rule_then')}</div>
            <div className="flex gap-2">
              <Select value={draft.section_id || sections[0]?.id || ''} onValueChange={(section_id) => setDraft({ ...draft, section_id })}>
                <SelectTrigger className="flex-1" data-testid="rule-section"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" onClick={add} data-testid="rule-add"><Plus className="w-4 h-4 mr-1.5" />{t('spotlight.add_rule')}</Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="text-sm font-medium text-foreground">{t('spotlight.limit')}</div>
            <div className="flex gap-2">
              <Input type="number" min={1} max={500} value={limitText} placeholder="-" className="w-28"
                onChange={(e) => setLimitText(e.target.value)} data-testid="spotlight-limit" />
              <Button type="button" variant="outline" onClick={saveLimit}>{t('common.save')}</Button>
            </div>
            <p className="text-xs text-muted-foreground">{t('spotlight.limit_hint')}</p>
          </div>
        </ResponsiveModalBody>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
};

export default SpotlightRules;
